const STORAGE_KEY = 'loans_oidc_pkce_state';
export const APP_SELECTOR_RETURN_TO = '__app_selector__';

interface PendingAuthState {
  state: string;
  verifier: string;
  returnTo: string;
}

interface BrowserSessionResponse {
  authenticated: boolean;
  expiresIn: number;
}

type BrowserSessionEnvelope = {
  data?: BrowserSessionResponse;
};

function isBrowserSessionResponse(value: unknown): value is BrowserSessionResponse {
  return (
    typeof value === 'object' &&
    value !== null &&
    'authenticated' in value &&
    'expiresIn' in value &&
    typeof (value as BrowserSessionResponse).authenticated === 'boolean' &&
    typeof (value as BrowserSessionResponse).expiresIn === 'number'
  );
}

function unwrapBrowserSessionResponse(
  payload: BrowserSessionEnvelope | BrowserSessionResponse,
): BrowserSessionResponse {
  if (isBrowserSessionResponse(payload)) {
    return payload;
  }

  if (isBrowserSessionResponse(payload.data)) {
    return payload.data;
  }

  throw new Error('Invalid browser session response');
}

function apiBaseUrl() {
  return (import.meta.env.VITE_API_URL || '').replace(/\/$/, '');
}

function issuer() {
  return (import.meta.env.VITE_KEYCLOAK_ISSUER || '').replace(/\/$/, '');
}

function clientId() {
  return import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'public-vestra-loans';
}

function redirectUri() {
  return import.meta.env.VITE_KEYCLOAK_REDIRECT_URI || `${window.location.origin}/auth/callback`;
}

function postLogoutRedirectUri() {
  const configuredPostLogout = import.meta.env.VITE_KEYCLOAK_POST_LOGOUT_REDIRECT_URI;
  const localLoansLoginUrl = `${window.location.origin}/login`;

  if (configuredPostLogout) {
    try {
      const resolved = new URL(configuredPostLogout, window.location.origin);
      const isAppSelectorRedirect = resolved.origin !== window.location.origin;

      if (!isAppSelectorRedirect) {
        return resolved.toString();
      }
    } catch {
      // Fall through to the local-safe default.
    }
  }

  return localLoansLoginUrl;
}

function base64UrlEncode(input: ArrayBuffer | Uint8Array): string {
  const bytes = input instanceof Uint8Array ? input : new Uint8Array(input);
  let binary = '';
  bytes.forEach(byte => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

async function sha256(input: string) {
  return crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
}

function randomString() {
  return base64UrlEncode(crypto.getRandomValues(new Uint8Array(32)));
}

function getPendingState(): PendingAuthState | null {
  const raw = sessionStorage.getItem(STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as PendingAuthState;
  } catch {
    sessionStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

export async function startKeycloakLogin(returnTo?: string) {
  const state = randomString();
  const verifier = randomString();
  const challenge = base64UrlEncode(await sha256(verifier));
  const pendingState: PendingAuthState = {
    state,
    verifier,
    returnTo:
      returnTo || `${window.location.pathname}${window.location.search}${window.location.hash}`,
  };

  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(pendingState));

  const params = new URLSearchParams({
    client_id: clientId(),
    redirect_uri: redirectUri(),
    response_type: 'code',
    scope: 'openid profile email organization roles',
    code_challenge: challenge,
    code_challenge_method: 'S256',
    state,
  });

  window.location.assign(`${issuer()}/protocol/openid-connect/auth?${params.toString()}`);
}

export async function exchangeAuthCode(code: string, state: string) {
  const pending = getPendingState();
  if (!pending || pending.state !== state) {
    throw new Error('Invalid login state. Please try again.');
  }

  const response = await fetch(`${apiBaseUrl()}/api/v1/auth/keycloak/exchange`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      code,
      codeVerifier: pending.verifier,
      redirectUri: redirectUri(),
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to create a Loans browser session');
  }

  const session = unwrapBrowserSessionResponse(
    (await response.json()) as BrowserSessionEnvelope | BrowserSessionResponse,
  );
  sessionStorage.removeItem(STORAGE_KEY);
  return {
    expiresIn: session.expiresIn,
    returnTo: pending.returnTo,
  };
}

export async function refreshKeycloakSession() {
  const response = await fetch(`${apiBaseUrl()}/api/v1/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: '{}',
  });

  if (!response.ok) {
    const error = new Error('Failed to refresh Loans browser session') as Error & {
      status?: number;
    };
    error.status = response.status;
    throw error;
  }

  return unwrapBrowserSessionResponse(
    (await response.json()) as BrowserSessionEnvelope | BrowserSessionResponse,
  );
}

export function buildKeycloakLogoutUrl() {
  const params = new URLSearchParams({
    post_logout_redirect_uri: postLogoutRedirectUri(),
    client_id: clientId(),
  });

  return `${issuer()}/protocol/openid-connect/logout?${params.toString()}`;
}
